/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * DOCUMENTS FIRST: WHAT A TYPED SUBMIT DOES (v1_894).
 *
 * The client chats with Alex while the documents are still being read. When the
 * reading lands, Alex gets the file and puts its findings to the client (a
 * permission the register record does not show for an activity they described,
 * a missing document). Release walk w791b654b: the client typed SUBMIT while the
 * reading was still running and was asked to wait; the reading landed, and the
 * client's next SUBMIT went through seconds later, before Alex had said a word
 * with the file. The findings never reached the client, so nothing was raised
 * in the interview and the officer had to ask by request instead.
 *
 * Now a SUBMIT is accepted only once Alex has spoken with the file:
 *   'hold_analysis'       the reading is still running: say so, keep the chat open
 *   'raise_points_first'  the reading has landed but Alex has not yet replied
 *                         with it: this turn Alex says the reading has finished
 *                         and raises any point not yet covered, then asks for
 *                         SUBMIT again (or, with nothing to raise, gives the
 *                         short summary and asks for SUBMIT)
 *   'submit'              Alex has spoken with the file: the file goes
 *   'none'                not a SUBMIT, or not a documents first case
 * A return visit after the first submission is never held here.
 */
export function isTypedSubmit(text) {
  const t = String(text == null ? '' : text).trim();
  return /^submit[.!]?$/i.test(t) || /^yes,?\s+submit/i.test(t) || t.toLowerCase().includes('**submit**');
}

export function dfSubmitDecision({ typedSubmit, isDf, boardReady, spokenWithFile, submittedAt }) {
  if (!typedSubmit || !isDf) return 'none';
  if (!boardReady) return 'hold_analysis';
  if (submittedAt) return 'submit';
  return spokenWithFile ? 'submit' : 'raise_points_first';
}

// The instruction that rides with the client's SUBMIT on a 'raise_points_first'
// turn. The client's own message stays "SUBMIT" in the transcript.
export const RAISE_POINTS_FIRST = '[SYSTEM: The client has typed SUBMIT, but your reading of their documents finished only moments ago and you have not yet spoken with them about it. Do not submit, and do not thank them for submitting. Say briefly that you have just finished reading their documents. Then raise, one at a time, any point from the file you have not already covered with them, above all any activity they described that the register record on file does not show a permission for. Once their answers are in, ask them to type SUBMIT. If there is genuinely nothing to raise, say so, give the short summary, and ask them to type SUBMIT when ready.]';

// The line added to Alex's instructions while the reading is still running.
export const NO_CLOSE_WHILE_READING = '\n\nWHILE YOU ARE STILL READING THEIR DOCUMENTS: do not give a closing summary and do not invite the client to type SUBMIT. The findings from their documents may still need to be put to them. When you have covered the points above, tell them warmly that you are finishing the review of their documents and will come straight back to them, and stop there.';

// FILE SENT, THEN NOT SENT (walk w73bbfaa0, 11/10/2026). The client typed SUBMIT
// while the reading was still running. The page asked Alex for a reply first and
// decided afterwards, so Alex answered "Your file has been sent to the compliance
// team", then the page held the submission. Two messages later Alex had to take
// it back. Now:
//   'hold_analysis'  Alex is not asked; the page shows and keeps SUBMIT_HOLD_TEXT.
//   'submit'         the server decides whether one open point is asked first
//                    (submitHold, see settled-points.js openPointsInVisit). If
//                    not, Alex is not asked at all: the page writes the
//                    submission, and only once that write has succeeded shows
//                    submittedText. A failed write shows SUBMIT_NOT_SAVED_TEXT.
// Nothing Alex writes can say the file has gone before it has.
export const SUBMIT_HOLD_TEXT = "Thank you, I'm just finishing the analysis of your documents (you can see the progress above). Your file has not been sent yet. The moment the analysis completes, type SUBMIT once more and your file will go to the compliance team.";
export const SUBMIT_NOT_SAVED_TEXT = 'Your file has not been sent yet, because it could not be saved just now. Nothing you have given me is lost. Please type SUBMIT again in a moment.';
export function submittedText({ firstName, firmName } = {}) {
  const who = String(firstName || '').trim();
  const firm = String(firmName || '').trim();
  return 'Thank you' + (who ? ', ' + who : '') + '. Your file has been sent to the compliance team' + (firm ? ' at ' + firm : '')
    + '. Anything still open stays in your checklist, where you can answer it or upload it at any time. The team will be in touch if anything else is needed.';
}

// In Alex's instructions until the file has gone, on both onboarding modes.
export const FILE_NOT_SENT_RULE = '\n\nTHE FILE HAS NOT BEEN SENT: this client\'s file has not been submitted yet. Never say or suggest that it has been sent, submitted, passed on, released or is with the compliance team, even when the client types SUBMIT. Only the system confirms a submission, after it has actually gone.';
